import { Controller } from "@hotwired/stimulus"
import { isValueChangingKey } from "../keyboard";

export default class extends Controller {
  static targets = [
    'input',
    'results',
    'idField',
    'nameField',
    'autocomplete'
  ];

  clear() {
    // We call this function whenever autocomplete fetches new results, because
    // this indicates that the user is actively looking for a new value to pick,
    // so meanwhile we should clear any previously set values from the ID and
    // Name hidden fields.
    this.idFieldTarget.value = '';
    this.nameFieldTarget.value = '';

    this.triggerChangeEvent();
  }

  fetch(e) {
    // The underlying autocomplete component is hard-wired to NOT fetch results
    // if the input is empty.  But we sometimes want that behavior, to show the
    // user options as soon as they click into the box (or if they clear the box).
    // So, we force it here.

    if (this.inputTarget.value.trim() !== '') {
      // The autocomplete controller handles non-empty input.
      return;
    }

    // If the input field is empty, ensure the id field is cleared.
    this.idFieldTarget.value = '';

    // In some cases (e.g. location picker) we don't want to show any results
    // until the user types at least one letter.
    const minChars = parseInt(this.context.element.getAttribute("data-min-chars"));
    if (minChars > 0) {
      return;
    }

    // If it's a keystroke that doesn't change the text query, do nothing.  This
    // could be a user hitting up/down arrows to navigate results.
    if (e.key && !isValueChangingKey(e.which)) {
      return;
    }

    const controller = this.application.getControllerForElementAndIdentifier(this.autocompleteTarget, "autocomplete");
    controller.fetchResults('');
  }

  change(e) {
    if (e.detail.value == "not-pickable") {
      // If the selected option is not meant to be pickable, do nothing.
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
    } else if (e.detail.value == "help") {
      // If the selected option is "help", just show Front chat.
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      window.FrontChat('show');
    } else if (e.detail.value.indexOf("inline:") == 0) {
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      var p = e.detail.value.split("inline:")[1].trim();
      Turbo.visit(p);
    } else if (e.detail.value.indexOf("drawer:") == 0) {
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      var event = new Event("drawer.open");
      event.url = e.detail.value.split("drawer:")[1].trim();

      // If there's a "context" attribute, pass it along.
      var context = this.element.getAttribute("data-context");
      if (context.length > 0) {
        event.context = context;
      }

      document.dispatchEvent(event);
    } else if (e.detail.value.indexOf('http') == 0 || e.detail.value.indexOf("/") == 0) {
      // If the selected option is a link, open it in a different tab.
      this.idFieldTarget.value = "";
      this.nameFieldTarget.value = "";
      this.inputTarget.value = "";
      window.open(e.detail.value, '_blank');
    } else {
      // Copy the ID and Name of the selected item to the hidden inputs.
      this.idFieldTarget.value = e.detail.value;
      this.nameFieldTarget.value = e.detail.textValue;

      // The default behavior of the autocomplete component is that if you
      // provide both an ID and a Name, it will write the ID into the text
      // input field.  We don't want that, so we override this behavior by
      // explicitly writing the Name.  Moreover, we try to identify the
      // "main" label (e.g. just the insurer name, without the city/state).
      if (e.detail.selected.innerHTML.indexOf('option-heading') >= 0) {
        this.inputTarget.value = $(e.detail.selected.innerHTML)[0].innerText;
      } else {
        this.inputTarget.value = e.detail.textValue;
      }

      // If desired, submit the form.
      if (this.element.getAttribute("data-autosubmit") == "true") {
        this.element.closest("form").requestSubmit();
      }
    }

    this.triggerChangeEvent();
  }

  triggerChangeEvent() {
    // Changes to the hidden element don't automatically trigger a change event,
    // so we have to trigger it manually.  Moreover, it has to be done as a DOM
    // event, not as a jQuery event.
    this.idFieldTarget.dispatchEvent(new Event("input"));
    this.idFieldTarget.dispatchEvent(new Event("change"));
    this.nameFieldTarget.dispatchEvent(new Event("input"));
    this.nameFieldTarget.dispatchEvent(new Event("change"));
  }
}
